// Cortes de energia no período do histórico (pedido 2026-10-07: "quero que
// fique destacado a hora que teve corte de energia, e se não teve, informe
// que não houve"). O Traccar não tem relatório de "corte de energia" pronto:
// o sinal vem como alarme — no evento (type "alarm", attributes.alarm) e/ou
// na própria posição (attributes.alarm). Conferido no banco em 07/10: os
// códigos que de fato chegam nesta frota são powerCut, powerOff e
// lowBattery ("Bateria violada" neste sistema — mesmo critério de
// powerStateFromAlarm em vehicles.service.ts). O retorno vem como
// powerRestored/powerOn, ou pela posição voltar com attributes.charge=true
// (GT06 manda esse flag de alimentação externa no pacote de status). Também
// detecta corte SEM alarme, pela queda do charge — ver abaixo.
import type { DetailedPosition, VehicleEvent } from "@/types";

const CUT_LABELS: Record<string, string> = {
  powerCut: "Corte de energia",
  powerOff: "Energia desligada",
  lowBattery: "Bateria violada",
  tamper: "Violação",
};
const RESTORE_CODES = new Set(["powerRestored", "powerOn"]);

export interface PowerCut {
  startTime: string;
  // undefined = a energia não voltou até o fim do período consultado.
  endTime?: string;
  durationMs?: number;
  label: string;
  lat?: number;
  lng?: number;
  address?: string | null;
  // Tensão da alimentação na última posição antes do corte (se o rastreador manda).
  voltageBefore?: number;
  // Última leitura ainda COM energia — o corte aconteceu entre ela e startTime
  // (o GT06 sem alarme só reporta no próximo pacote de status).
  lastPoweredAt?: string;
  // A primeira leitura do período já era "sem energia": o corte foi antes.
  startedBeforePeriod?: boolean;
}

interface Signal {
  t: number;
  kind: "cut" | "restore";
  label?: string;
  pos?: DetailedPosition;
  lastPoweredAt?: number;
  startedBeforePeriod?: boolean;
}

// O Traccar junta alarmes simultâneos com vírgula ("vibration,powerCut" —
// visto no banco em 07/10), então procura o código dentro da lista.
function alarmCodes(attrs: Record<string, unknown> | undefined): string[] {
  const a = attrs?.alarm;
  return typeof a === "string" ? a.split(",").map((c) => c.trim()) : [];
}
function cutCode(attrs: Record<string, unknown> | undefined): string | undefined {
  return alarmCodes(attrs).find((c) => c in CUT_LABELS);
}
function hasRestoreCode(attrs: Record<string, unknown> | undefined): boolean {
  return alarmCodes(attrs).some((c) => RESTORE_CODES.has(c));
}

// Posição mais próxima no tempo (busca binária) — eventos não trazem lat/lng.
function nearestPosition(positions: DetailedPosition[], t: number): DetailedPosition | undefined {
  if (positions.length === 0) return undefined;
  let lo = 0;
  let hi = positions.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (new Date(positions[mid].timestamp).getTime() < t) lo = mid + 1;
    else hi = mid;
  }
  const a = positions[lo];
  const b = positions[lo - 1];
  if (!b) return a;
  return Math.abs(new Date(a.timestamp).getTime() - t) <
    Math.abs(new Date(b.timestamp).getTime() - t)
    ? a
    : b;
}

export function powerAlarmLabel(attrs: Record<string, unknown> | undefined): string | undefined {
  const cut = cutCode(attrs);
  if (cut) return CUT_LABELS[cut];
  const codes = alarmCodes(attrs);
  if (codes.includes("powerRestored")) return "Energia restaurada";
  if (codes.includes("powerOn")) return "Energia ligada";
  return undefined;
}

const VOLTAGE_ON = 6;
const VOLTAGE_OFF = 3;
const CABLE_LABEL = "Alimentação cortada (bateria/cabo desconectado)";

export interface VoltageReading {
  t: number;
  volts: number;
  pos: DetailedPosition;
}

// Leituras de tensão da bateria do VEÍCULO (não a bateria interna do
// rastreador), em ordem de chegada.
export function vehicleVoltageReadings(positions: DetailedPosition[]): VoltageReading[] {
  const gt06 = positions.some((p) => typeof p.attributes?.charge === "boolean");
  const out: VoltageReading[] = [];
  for (const p of positions) {
    const a = p.attributes ?? {};
    const v =
      typeof a.power === "number" ? a.power : gt06 && typeof a.adc1 === "number" ? a.adc1 : undefined;
    if (v == null || v < 0 || v > 60) continue;
    out.push({ t: positionTime(p), volts: v, pos: p });
  }
  return out.sort((a, b) => a.t - b.t);
}

// Pacote de status do GT06 (sem GPS: sem "sat") herda o fixTime da última
// posição — um "charge=false" recebido às 17:20 aparece com fixTime 17:04.
// Pra esses, a hora real é a de chegada no servidor.
function positionTime(p: DetailedPosition): number {
  if (p.serverTime && !("sat" in (p.attributes ?? {}))) return new Date(p.serverTime).getTime();
  return new Date(p.timestamp).getTime();
}

export function detectPowerCuts(
  positions: DetailedPosition[],
  events: VehicleEvent[],
): PowerCut[] {
  const signals: Signal[] = [];
  for (const e of events) {
    if (e.type !== "alarm") continue;
    const code = cutCode(e.attributes);
    const t = new Date(e.eventTime).getTime();
    if (code) signals.push({ t, kind: "cut", label: CUT_LABELS[code] });
    else if (hasRestoreCode(e.attributes)) signals.push({ t, kind: "restore" });
  }
  for (const p of positions) {
    const code = cutCode(p.attributes);
    if (code) signals.push({ t: positionTime(p), kind: "cut", label: CUT_LABELS[code], pos: p });
    else if (hasRestoreCode(p.attributes))
      signals.push({ t: positionTime(p), kind: "restore", pos: p });
  }

  // Tensão da bateria do veículo, quando o rastreador manda (GT06 envia a
  // cada minuto no pacote 0x94, que o Traccar grava como "adc1"; outros
  // protocolos usam "power"). É o sinal mais preciso: no caso RAB-8J81 foi
  // 12,2 V às 17:19:57 → 0,0 V às 17:20:28. adc1 só é tratado como tensão
  // em rastreador que também manda "charge" (família GT06) — em outros
  // modelos adc1 pode ser outro sensor.
  const voltages = vehicleVoltageReadings(positions);
  for (let i = 0; i < voltages.length; i++) {
    const v = voltages[i];
    const prev = voltages[i - 1];
    const next = voltages[i + 1];
    if (v.volts >= VOLTAGE_ON) {
      if (prev && prev.volts <= VOLTAGE_OFF) signals.push({ t: v.t, kind: "restore", pos: v.pos });
      continue;
    }
    if (v.volts > VOLTAGE_OFF) continue;
    if (!prev || prev.volts <= VOLTAGE_OFF) {
      if (!prev && (!next || next.volts <= VOLTAGE_OFF))
        signals.push({ t: v.t, kind: "cut", label: CABLE_LABEL, pos: v.pos, startedBeforePeriod: true });
      continue;
    }
    if (next && next.volts > VOLTAGE_OFF) continue;
    signals.push({ t: v.t, kind: "cut", label: CABLE_LABEL, pos: v.pos, lastPoweredAt: prev.t });
  }

  // Corte sem alarme (caso real 07/10, KWID RAB-8J81: bateria do carro
  // roubada e o rastreador GT06 nunca mandou "powerCut" — só passou a
  // reportar charge=false no pacote de status, rodando na bateria interna).
  // Conta como corte quando charge vira false E a leitura seguinte confirma
  // (ou é a última do período) — um false isolado entre trues é oscilação
  // (visto no device 5142).
  const charges = positions
    .filter((p) => typeof p.attributes?.charge === "boolean")
    .map((p) => ({ t: positionTime(p), on: p.attributes.charge === true, pos: p }))
    .sort((a, b) => a.t - b.t);
  for (let i = 0; i < charges.length; i++) {
    const c = charges[i];
    const prev = charges[i - 1];
    const next = charges[i + 1];
    if (c.on) {
      signals.push({ t: c.t, kind: "restore", pos: c.pos });
      continue;
    }
    if (prev && !prev.on) continue;
    if (next && next.on) continue;
    signals.push({
      t: c.t,
      kind: "cut",
      label: CABLE_LABEL,
      pos: c.pos,
      lastPoweredAt: prev?.t,
      startedBeforePeriod: !prev,
    });
  }
  // Mesmo instante: o corte vem antes do retorno.
  signals.sort((a, b) => a.t - b.t || (a.kind === "cut" ? -1 : 1));

  const cuts: PowerCut[] = [];
  let open: PowerCut | null = null;
  for (const s of signals) {
    if (s.kind === "cut") {
      // Alarme repetido (evento + posição do mesmo corte, ou o rastreador
      // reenviando) enquanto o corte segue aberto — é o mesmo corte.
      if (open) {
        // Mesmo corte visto por outro sinal (charge × tensão × alarme): fica
        // com a janela mais estreita ("última leitura com energia" mais
        // recente) — a tensão chega a cada minuto, o status só a cada ~15.
        const openStart = new Date(open.startTime).getTime();
        if (
          s.lastPoweredAt != null &&
          s.t - openStart < 10 * 60_000 &&
          (open.lastPoweredAt == null || s.lastPoweredAt > new Date(open.lastPoweredAt).getTime())
        ) {
          open.lastPoweredAt = new Date(s.lastPoweredAt).toISOString();
        }
        if (open.voltageBefore == null && s.lastPoweredAt != null) {
          const before = voltages.filter((v) => v.t <= s.lastPoweredAt!).pop();
          if (before) open.voltageBefore = before.volts;
        }
        continue;
      }
      const pos = s.pos ?? nearestPosition(positions, s.t);
      const voltageBefore = voltages.filter((v) => v.t < s.t && v.volts > VOLTAGE_OFF).pop()?.volts;
      open = {
        startTime: new Date(s.t).toISOString(),
        label: s.label ?? "Corte de energia",
        lat: pos?.lat,
        lng: pos?.lng,
        address: pos?.address,
        voltageBefore,
        lastPoweredAt:
          s.lastPoweredAt != null ? new Date(s.lastPoweredAt).toISOString() : undefined,
        startedBeforePeriod: s.startedBeforePeriod,
      };
      cuts.push(open);
    } else if (open) {
      const start = new Date(open.startTime).getTime();
      if (s.t <= start) continue;
      open.endTime = new Date(s.t).toISOString();
      open.durationMs = s.t - start;
      open = null;
    }
  }
  return cuts;
}
